import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import {RootState} from '@/app/store';
import {HttpMethod} from '@/constants/httpMethod';
import {EReducersPath} from '@/dataSource/utils/ReducerPaths';

import {setBearerTokenIfExists, setWafHeaders} from '../headers';
import {reAuth} from '../reAuth';

import {apiBaseSplitBills} from './constants';
import {
  IGetUploadedBillResultResponse,
  IMultiPurposeChatRequest,
  IMultiPurposeChatResponse,
  IMultiPurposeChatSessionResponse,
  IPostUploadSplitBillResponse,
  ISplitBillsRequest,
  ISplitBillsResponse,
  PostFreshchatRestoreIdRequest,
  PostFreshchatRestoreIdResponse,
} from './entities';

const baseQuery = async (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    timeout: 30 * 1000,
    prepareHeaders: async (headers, {getState}) => {
      const token = (getState() as RootState).tokenDataSlice?.tokenData
        ?.accessToken;
      setBearerTokenIfExists(headers, token);
      await setWafHeaders(baseUrl, headers);
      return headers;
    },
  });

const baseApiQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const baseUrl = apiBaseSplitBills.baseUrl;

  return reAuth(args, api, extraOptions, baseUrl, baseQuery);
};

export const splitBillsApi = createApi({
  reducerPath: EReducersPath.SPLIT_BILLS_API,
  refetchOnReconnect: true,
  baseQuery: baseApiQueryWithReAuth,
  endpoints: build => ({
    splitBills: build.mutation<ISplitBillsResponse, ISplitBillsRequest>({
      query: body => ({
        url: apiBaseSplitBills.endpoints.splitBills,
        method: HttpMethod.POST,
        body,
      }),
    }),
    postBillImage: build.mutation<IPostUploadSplitBillResponse, any>({
      query: body => ({
        url: apiBaseSplitBills.endpoints.postUploadBillImage,
        method: HttpMethod.POST,
        body,
      }),
    }),
    postFreshchatRestoreId: build.mutation<
      PostFreshchatRestoreIdResponse,
      PostFreshchatRestoreIdRequest
    >({
      query: body => {
        return {
          url: apiBaseSplitBills.endpoints.postFreshchatRestoreId,
          method: HttpMethod.POST,
          body: {session_id: body.session_id},
        };
      },
    }),

    getPostBillImageResult: build.query<IGetUploadedBillResultResponse, string>(
      {
        query: sessionId => ({
          url: `${apiBaseSplitBills.endpoints.getUploadBillImageResult}/${sessionId}`,
        }),
      },
    ),
    sendMultiPurposeChat: build.mutation<
      IMultiPurposeChatResponse,
      IMultiPurposeChatRequest
    >({
      query: body => ({
        url: apiBaseSplitBills.endpoints.multiPurposeChat,
        method: HttpMethod.POST,
        body,
      }),
    }),
    getMultiPurposeChatSession: build.query<
      IMultiPurposeChatSessionResponse,
      string
    >({
      query: sessionId => ({
        url: apiBaseSplitBills.endpoints.multiPurposeChat,
        params: {session_id: sessionId},
      }),
    }),
  }),
});

export const {
  useSplitBillsMutation,
  usePostBillImageMutation,
  useLazyGetPostBillImageResultQuery,
  useSendMultiPurposeChatMutation,
  usePostFreshchatRestoreIdMutation,
  useLazyGetMultiPurposeChatSessionQuery,
} = splitBillsApi;
